import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Custom hook for managing user industries
 * Handles fetching context, updating industries, and state management
 */
const useIndustryManagement = () => {
  const [context, setContext] = useState(null);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const fetchContext = useCallback(async (userId) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/users/${userId}/industries/context`
      );
      const data = await response.json();
      
      if (data.success) {
        setContext(data.context);
        setSelectedIndustries(data.context.current_industries || []);
        return data.context;
      }
      throw new Error('Failed to fetch context');
    } catch (error) {
      console.error('Error fetching context:', error);
      toast.error('Failed to load industry context');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateIndustries = useCallback(async (userId, industries) => {
    setIsSaving(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/users/${userId}/industries`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ industries })
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Industries updated successfully');
        
        if (data.warnings && data.warnings.length > 0) {
          data.warnings.forEach(warning => {
            toast(warning, { icon: '⚠️', duration: 5000 });
          });
        }
        
        if (data.affected_members && data.affected_members.length > 0) {
          toast.success(
            `Updated. ${data.affected_members.length} team member(s) affected.`,
            { duration: 5000 }
          );
        }
        
        return { success: true, data };
      }
      
      throw new Error(data.message || 'Failed to update industries');
    } catch (error) {
      console.error('Error updating industries:', error);
      toast.error(`Failed to update: ${error.message}`);
      return { success: false, error: error.message };
    } finally {
      setIsSaving(false);
    }
  }, []);

  const toggleIndustry = useCallback((industry) => {
    setSelectedIndustries(prev =>
      prev.includes(industry)
        ? prev.filter(i => i !== industry)
        : [...prev, industry]
    );
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIndustries([]);
  }, []);

  const resetToOriginal = useCallback(() => {
    if (context) {
      setSelectedIndustries(context.current_industries || []);
    }
  }, [context]);

  return {
    context,
    selectedIndustries,
    isLoading,
    isSaving,
    fetchContext,
    updateIndustries,
    toggleIndustry,
    clearSelection,
    resetToOriginal,
    setSelectedIndustries
  };
};

export default useIndustryManagement;
